"use client";

import {
	Button,
	type DefaultImageProps,
	Heading,
	Image,
	Tag
} from "@pamperedchef/design-system";
import type { ComponentProps } from "react";

import { RichText } from "@/composites/foundation";
import { SectionHeading } from "@/composites/landing/SectionHeading";
import type { CmsImage } from "@/composites/landing/types";
import * as styles from "./FeatureCards.module.scss";

export type FeatureCardTagColor = ComponentProps<typeof Tag>["color"];

export interface FeatureCardTag {
	label: string;
	color?: FeatureCardTagColor;
	/** Design-system icon name, e.g. `chef-hat` or `star`. */
	icon?: string;
}

export interface FeatureCardCta {
	title: string;
	href: string;
	openInNewTab?: boolean;
	assistiveText?: string;
}

export interface FeatureCardItem {
	image?: CmsImage;
	tag?: FeatureCardTag;
	heading: string;
	/** HTML. */
	copy?: string;
	cta?: FeatureCardCta;
}

export interface FeatureCardsProps {
	heading?: string;
	cards: FeatureCardItem[];
	/** Navigation stays with the caller; composites do not touch `window`. */
	onCtaPress?(cta: FeatureCardCta): void;
}

/** Two promotional cards under a shared section heading. */
export function FeatureCards({
	heading,
	cards,
	onCtaPress
}: FeatureCardsProps) {
	if (!cards.length) return null;

	return (
		<section
			className={styles["feature-cards"]}
			data-component="FeatureCards"
		>
			<SectionHeading headline={heading} alignment="center" />

			<ul className={styles["feature-cards__grid"]}>
				{cards.map((card, index) => (
					<li
						key={`${card.heading}-${index}`}
						className={styles["feature-card"]}
					>
						{card.image ? (
							<div className={styles["feature-card__media"]}>
								<Image<DefaultImageProps>
									src={card.image.url}
									alt={card.image.alt ?? ""}
									className={styles["feature-card__image"]}
									// @ts-expect-error DS types only the ratio key both sets share (1:1); 3:2 exists at runtime.
									aspectRatio="3:2"
									objectFit="cover"
								/>
							</div>
						) : null}

						<div className={styles["feature-card__body"]}>
							{card.tag ? (
								<Tag
									kind="subtle"
									color={card.tag.color ?? "lime-green"}
									size="xs"
									label={card.tag.label}
									icon={
										card.tag.icon
											? { name: card.tag.icon }
											: undefined
									}
									className={styles["feature-card__eyebrow"]}
								/>
							) : null}
							<Heading
								tag="h3"
								className={styles["feature-card__heading"]}
							>
								{card.heading}
							</Heading>
							{card.copy ? (
								<RichText
									content={card.copy}
									className={styles["feature-card__copy"]}
								/>
							) : null}
							{card.cta ? (
								<Button
									kind="secondary"
									size="sm"
									className={styles["feature-card__cta"]}
									aria-label={
										card.cta.assistiveText || undefined
									}
									onPress={() => onCtaPress?.(card.cta!)}
								>
									{card.cta.title}
								</Button>
							) : null}
						</div>
					</li>
				))}
			</ul>
		</section>
	);
}
